import { useEffect, useState } from "react";
import { GameChallenges, getGameChallenges, getTeamChallenges } from "../../util/Requests";

export type ChallengeState =
  | { status: "loading" }
  | { status: "ok"; data: GameChallenges }
  | { status: "error" };

/** A game's coach's challenges. `enabled` false (a season report) asks for
 * nothing. */
export const useChallengeQuery = (
  gameId?: string,
  enabled = true,
): ChallengeState => {
  const [state, setState] = useState<ChallengeState>({ status: "loading" });
  useEffect(() => {
    if (!enabled) return;
    // No game id means nothing to ask for — settle instead of waiting forever,
    // or the ready flag would never set.
    if (!gameId) {
      setState({ status: "error" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    getGameChallenges(gameId)
      .then((response) => {
        if (!cancelled) setState({ status: "ok", data: response?.data ?? {} });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, enabled]);
  return state;
};

/** The core season type a report's season type reads as ("Regular Season" → regular). */
const coreSeasonType = (t?: string) =>
  /play/i.test(t ?? "") ? "playoffs" : /pre/i.test(t ?? "") ? "preseason" : "regular";

/** A season's (or a window's) challenges in the game hook's shape: the team under its abbreviation, its opponents
 * together under "OPP". `enabled` false (a game report) asks for nothing. */
export const useTeamChallengeTotals = (
  teamNbaId: string | undefined,
  abbrev: string,
  season: string | undefined,
  seasonType: string | undefined,
  enabled = true,
  /** A window's games (core ids); the whole season without */
  games?: string[],
): ChallengeState => {
  const [state, setState] = useState<ChallengeState>({ status: "loading" });
  const gameKey = (games ?? []).join(",");
  useEffect(() => {
    if (!enabled) return;
    if (!teamNbaId || !season) {
      setState({ status: "error" });
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    getTeamChallenges(teamNbaId, abbrev, season, coreSeasonType(seasonType), gameKey ? gameKey.split(",") : undefined)
      .then((response) => {
        if (cancelled) return;
        const d = response?.data;
        setState(
          d ? { status: "ok", data: { [abbrev]: d.team, OPP: d.opponents } } : { status: "error" },
        );
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [teamNbaId, abbrev, season, seasonType, enabled, gameKey]);
  return state;
};
